import { getObjectDetection } from '@api/cadFile.js';
import toast from '@components/Common/Toast.js';
import { Toggle } from '@components/Common/Toggle.js';
import {
  FileIcon,
  ModelIcon,
  PanoramaIcon,
  PdfIcon,
  VideoIcon,
} from '@icons/index.js';
import { CadFileState, IsAutoDetectionAllowedState } from '@states/Viewer.js';
import { UIDiv, UIInput, UIText } from '@ui';

export default class DragAndDrop extends UIDiv {
  constructor({
    // handleFileSelection = null,
    callBack = null,
    uploadText = i18next.t('pages.projects.dragAndDrop'),
    supportedTypesText = i18next.t('pages.projects.supportedTaskFiles'),
    acceptedFileTypes = '*', // we accept all files by default
    multiple = true,
    allowVideoFrameUpload = false,
  }) {
    // if (typeof handleFileSelection !== 'function') {
    //     throw new Error('handleFileSelection is required and must be a function');
    // }
    super();

    this.isLoading = false;
    this.selectedFiles = new Map();
    this.previewElementsMap = new Map();
    this.callBack = callBack;
    this.persistentErrorMsg = '';
    this.acceptedFileTypes = acceptedFileTypes;
    this.multiple = multiple;
    this.allowVideoFrameUpload = allowVideoFrameUpload;

    if (!this.callBack) {
      console.error('callBack function to pass uploaded files is missing');
      return;
    }

    this.setClass(
      'border-dashed border-2 border-gray-300 rounded-md p-4 ' +
        'flex flex-col items-center justify-center ' +
        'cursor-pointer hover:bg-gray-100 transition relative w-full',
    );

    // Hidden file input element
    this.fileInput = new UIInput('')
      .setType('file')
      .setAttribute('multiple', multiple)
      .setAttribute('accept', acceptedFileTypes)
      .setClass('hidden');

    // Upload text and supported types - only show when no files are selected
    this.uploadInstructionsContainer = new UIDiv().setClass(
      'flex flex-col items-center justify-center py-4',
    );

    this.uploadText = new UIText(uploadText).setClass('text-gray-500 mb-1');

    this.supportedTypesText = new UIText(supportedTypesText).setClass(
      'text-sm text-gray-400 text-center',
    );

    // Selected files container (inside the upload container)
    this.selectedFilesContainer = new UIDiv().setClass(
      'w-full flex flex-wrap gap-4 max-h-[40vh] overflow-y-auto overflow-x-hidden justify-center',
    );

    this.uploadInstructionsContainer.add(
      this.uploadText,
      this.supportedTypesText,
    );

    // Persistent error alert
    this.errorAlertContainer = new UIDiv();

    // Add all elements to the file input container
    this.add(
      this.errorAlertContainer,
      this.uploadInstructionsContainer,
      this.fileInput,
      this.selectedFilesContainer,
    );

    this.setupHandlers();
  }

  handleFileSelection(fileList) {
    // file validation with accepted file types and multiple files
    if (fileList.length === 0) return;
    for (let i = 0; i < fileList.length; i++) {
      // check multiple files
      if (!this.multiple && i > 0) {
        const errorMsg = i18next.t('pages.projects.onlyOneFileAllowed');
        this.persistentErrorMsg = errorMsg;
        this.renderErrorAlert();
        toast.error(errorMsg);
        return;
      }
      const file = fileList[i];
      const ext = file.name.split('.').pop().toLowerCase();
      if (
        !this.acceptedFileTypes.includes(ext) &&
        this.acceptedFileTypes !== '*'
      ) {
        const errorMsg = i18next.t('pages.projects.unsupportedFileType', {
          fileType: ext,
        });
        this.persistentErrorMsg = errorMsg;
        this.renderErrorAlert();
        toast.error(errorMsg);

        return;
      }
    }
    // const allowedExtensions = [
    //   'glb',
    //   'gltf',
    //   'stl',
    //   'step',
    //   'stp',
    //   'jpg',
    //   'jpeg',
    //   'png',
    //   'webp',
    //   'mp4',
    //   'mov',
    //   'pdf',
    // ];
    // Calculate total size of already selected files
    let currentTotalSize = 0;
    for (const fileData of this.selectedFiles.values()) {
      currentTotalSize += fileData.file.size;
    }

    // Calculate total size if new files are added
    let newFilesTotalSize = 0;
    Array.from(fileList).forEach(file => {
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
      if (!this.selectedFiles.has(fileKey)) {
        newFilesTotalSize += file.size;
      }
    });

    const MB_LIMIT = 500;
    const totalMB = (currentTotalSize + newFilesTotalSize) / (1024 * 1024);
    if (totalMB > MB_LIMIT) {
      const errorMsg =
        i18next.t('pages.projects.fileSizeLimitError', {
          limit: MB_LIMIT,
        }) || `Total file size must not exceed ${MB_LIMIT} MB.`;
      this.persistentErrorMsg = errorMsg;
      this.renderErrorAlert();
      if (typeof toast !== 'undefined') {
        toast.error(errorMsg);
      } else {
        console.error(errorMsg);
      }
      return;
    } else {
      // Clear persistent error if input is valid
      if (this.persistentErrorMsg) {
        this.persistentErrorMsg = '';
        this.renderErrorAlert();
      }
    }

    Array.from(fileList).forEach(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
      const detectedPosition = [];
      const detectionResponse = [];
      if (!this.selectedFiles.has(fileKey)) {
        this.selectedFiles.set(fileKey, {
          file,
          isPanorama: false,
          isUploaded: false,
          isUploading: false,
          isDetection: false,
          useVirtualTexture: false,
          uploadAsFrames: false,
          detectedPosition,
          detectionResponse,
        });
      }
    });
    this.callBack(this.selectedFiles);

    this.updateSelectedFilesDisplay();
  }

  renderErrorAlert() {
    this.errorAlertContainer.clear();
    if (this.persistentErrorMsg) {
      const alertDiv = new UIDiv()
        .setClass(
          'bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-2 text-sm w-full',
        )
        .setInnerText(this.persistentErrorMsg);
      this.errorAlertContainer.add(alertDiv);
    }
  }

  createFilePreview(fileData) {
    const { file, isUploaded, isUploading, isPanorama } = fileData;
    const isAnyFileUploading = Array.from(this.selectedFiles.values()).some(
      f => f.isUploading,
    );

    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;

    // Outer container with relative positioning - fixed height and width
    const container = new UIDiv().setClass(
      'relative bg-white border rounded p-2 flex flex-col h-64 w-full min-w-[16rem] max-w-[16rem] transition-all duration-300 ease-in-out',
    );
    container.onClick(event => {
      event.stopPropagation();
    });
    container.dom.dataset.fileKey = fileKey;
    if (isUploaded) {
      container.addClass('bg-green-50');
    } else if (isUploading) {
      container.addClass('bg-blue-50 loading-border');
    }

    // Preview container for image or file extension - fixed height
    const previewContainer = new UIDiv().setClass(
      'w-full h-36 mb-2 overflow-hidden rounded relative z-10 flex-shrink-0',
    );

    // Get file extension for icon selection
    const ext = file.name.split('.').pop().toLowerCase();

    if (file.type.startsWith('image/')) {
      const img = document.createElement('img');

      // Check if the file has been processed and has a new preview URL
      if (fileData.previewUrl) {
        img.src = fileData.previewUrl;
      } else {
        img.src = URL.createObjectURL(file);
      }

      //img.src = URL.createObjectURL(file);
      img.className = 'w-full h-full object-cover';
      img.setAttribute('draggable', 'false');
      previewContainer.dom.appendChild(img);
    } else {
      // Custom preview container with icon based on file type
      previewContainer.setClass(
        'flex flex-col items-center justify-center bg-white h-36 flex-shrink-0 rounded',
      );

      // Add appropriate icon based on file type
      let icon;
      let fileTypeLabel = '';

      if (['glb', 'gltf', 'stl', 'step', 'stp'].includes(ext)) {
        icon = new ModelIcon({ size: 48, color: 'none' });
        fileTypeLabel = '3D Model';
      } else if (ext === 'pdf') {
        icon = new PdfIcon({ size: 48, color: 'none' });
        fileTypeLabel = 'PDF';
      } else if (['mp4', 'mov'].includes(ext)) {
        icon = new VideoIcon({ size: 48, color: 'none' });
        fileTypeLabel = 'Video';
      } else {
        icon = new FileIcon({ size: 48, color: 'none' });
        fileTypeLabel = 'File';
      }

      // Add the icon
      previewContainer.add(icon);

      // Add the type label and extension
      previewContainer.add(
        new UIText(fileTypeLabel).setClass(
          'text-sm font-medium text-gray-700 mt-2',
        ),
        new UIText(ext.toUpperCase()).setClass(
          'text-xs font-bold text-gray-600 mt-1',
        ),
      );
    }

    const textContainer = new UIDiv().setClass(
      'flex flex-col p-1 relative z-10 flex-grow overflow-hidden',
    );

    const fileNameText = new UIText(file.name).setClass(
      'text-sm font-medium truncate',
    );

    const fileSizeText = new UIText(
      `${
        file.size < 1024 * 1024
          ? (file.size / 1024).toFixed(2) + ' KB'
          : (file.size / 1024 / 1024).toFixed(2) + ' MB'
      }`,
    ).setClass('text-xs text-gray-500');

    const fileTypeText = new UIText(file.type).setClass(
      'text-xs text-gray-400 italic truncate',
    );

    textContainer.add(fileNameText, fileSizeText, fileTypeText);

    if (ext === 'glb') {
      const fd = this.selectedFiles.get(fileKey);
      fd.useVirtualTexture = true;
      this.selectedFiles.set(fileKey, fd);
      const showWarning = file.size > 25 * 1024 * 1024;
      if (showWarning) {
        const infoText = new UIText(
          'Virtual texturing recommended',
          // i18next.t('pages.projects.glbFileWarning'),
        ).setClass('text-xs text-red-500');
        textContainer.add(infoText);
      }

      const onTextureToggle = state => {
        fd.useVirtualTexture = state;
        this.selectedFiles.set(fileKey, fd);
        this.callBack(this.selectedFiles);
      };
      const enableTexturing = new Toggle({
        label: 'Virtual Texturing',
        initialState: fd.useVirtualTexture,
        onChange: onTextureToggle,
      });
      enableTexturing.addClass('mt-2');
      enableTexturing.find('label')[0].setClass('pl-2 text-xs text-gray-500');
      textContainer.add(enableTexturing);
    }

    if (this.allowVideoFrameUpload && ['mp4', 'mov'].includes(ext)) {
      const fd = this.selectedFiles.get(fileKey);
      const uploadAsFramesToggle = new Toggle({
        label: 'Upload as frames',
        initialState: fd.uploadAsFrames,
        onChange: state => {
          fd.uploadAsFrames = state;
          this.selectedFiles.set(fileKey, fd);
          this.callBack(this.selectedFiles);
        },
      });
      uploadAsFramesToggle.addClass('mt-2');
      uploadAsFramesToggle
        .find('label')[0]
        .setClass('pl-2 text-xs text-gray-500');
      uploadAsFramesToggle.onClick(event => event.stopPropagation());
      textContainer.add(uploadAsFramesToggle);
    }

    if (file.type.startsWith('image/') && !isAnyFileUploading) {
      const panoramaButton = new PanoramaIcon({
        color: 'white',
        strokeColor: 'black',
        size: 30,
      }).setClass(
        'absolute top-2 left-2 cursor-pointer z-20 p-1 rounded-full  ' +
          (isPanorama
            ? 'fill-current text-blue-600'
            : 'fill-current text-gray-600 opacity-80'),
      );
      panoramaButton.onClick(event => {
        event.stopPropagation();
        const fileKey = container.dom.dataset.fileKey;
        const fd = this.selectedFiles.get(fileKey);
        if (fd) {
          fd.isPanorama = !fd.isPanorama;
          this.selectedFiles.set(fileKey, fd);
          this.updatePanoramaIcon(container, fd.isPanorama);
        }
      });

      container.add(panoramaButton);
    }

    // Remove button (top-right), only if no file is uploading
    if (!isAnyFileUploading) {
      const removeButton = new UIDiv()
        .setClass(
          'absolute top-3 right-3 text-red-500 cursor-pointer text-2xl font-bold z-20 bg-white bg-opacity-75 rounded-full w-5 h-5 flex items-center justify-center',
        )
        .setInnerText('×')
        .onClick(event => {
          event.stopPropagation();
          const fileKey = container.dom.dataset.fileKey;
          this.selectedFiles.delete(fileKey);
          this.removeFilePreviewElement(fileKey);
        });
      container.add(removeButton);
    }

    container.add(previewContainer, textContainer);
    return container;
  }

  updatePanoramaIcon(previewElement, isPanorama) {
    const panoramaIcon = previewElement.dom.querySelector(
      '.absolute.top-2.left-2',
    );
    if (panoramaIcon) {
      if (isPanorama) {
        panoramaIcon.classList.remove('text-gray-600', 'opacity-40');
        panoramaIcon.classList.add('text-blue-600');
      } else {
        panoramaIcon.classList.remove('text-blue-600');
        panoramaIcon.classList.add('text-gray-600', 'opacity-40');
      }
    }
  }

  async updateSelectedFilesDisplay() {
    this.selectedFilesContainer.clear();
    this.previewElementsMap.clear();

    if (this.selectedFiles.size > 0) {
      this.uploadInstructionsContainer.setClass('hidden');
    } else {
      this.uploadInstructionsContainer.setClass(
        'flex flex-col items-center justify-center py-4',
      );
    }

    this.updateModalWidth();

    for (const [fileKey, fileData] of this.selectedFiles) {
      if (IsAutoDetectionAllowedState.isAutoDetectionAllowed) {
        const formData = new FormData();

        if (fileData.file) {
          if (fileData.file instanceof Blob || fileData.file instanceof File) {
            formData.append('image', fileData.file);
          } else {
            console.error(
              'File is not a Blob or File. Actual type:',
              fileData.file,
            );
          }
        } else {
          console.error('fileData.file is undefined or null!');
        }

        formData.append('cadFileId', CadFileState.cadfile.cadFileID);

        try {
          const response = await getObjectDetection(formData);

          if (response.status !== 'success') {
            throw new Error('Failed to process image');
          }

          const detections = response.detections || [];

          if (detections.length > 0) {
            const blob = formData.get('image');

            const image = new Image();
            image.src = URL.createObjectURL(blob);

            image.onload = () => {
              // Create canvas to draw on
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');

              canvas.width = image.width;
              canvas.height = image.height;

              ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

              const positions = this.drawBoundingBoxes(ctx, detections, image);

              canvas.toBlob(processedBlob => {
                const processedFile = new File(
                  [processedBlob],
                  fileData.file.name,
                  {
                    type: 'image/jpeg',
                  },
                );

                fileData.file = processedFile;
                fileData.previewUrl = URL.createObjectURL(processedBlob);

                const fData = this.selectedFiles.get(fileKey);
                if (fData) {
                  fData.isDetection = true;
                  if (positions.length > 0) {
                    fData.detectedPosition = positions;
                    fData.detectionResponse = detections;
                  }
                  this.selectedFiles.set(fileKey, fData);
                }
                this.updateFilePreview(fileKey, fileData.previewUrl);
              }, 'image/jpeg');
            };
          }
        } catch (error) {
          console.error('Error:', error);
        }
      }

      const filePreview = this.createFilePreview(fileData);

      filePreview.addClass('opacity-0 scale-95');

      this.selectedFilesContainer.add(filePreview);
      this.previewElementsMap.set(fileKey, filePreview);

      setTimeout(() => {
        filePreview.removeClass('opacity-0 scale-95');
      }, 50);
    }
  }

  updateModalWidth() {
    const modalElement = document.querySelector('.modal-content');
    if (modalElement) {
      if (this.selectedFiles.size >= 4) {
        modalElement.classList.remove('max-w-xl');
        modalElement.classList.add('max-w-xxl');
      } else {
        modalElement.classList.remove('max-w-xxl');
        modalElement.classList.add('max-w-xl');
      }
    }
  }

  drawBoundingBoxes(ctx, detections, image) {
    if (!detections || !Array.isArray(detections) || detections.length === 0) {
      console.warn('No detections found, skipping drawing.');
      return [];
    }

    const position = [];

    const primaryColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--o-hive-primary-100')
      .trim();

    ctx.strokeStyle = primaryColor;
    ctx.lineWidth = 10;
    ctx.font = '12px Arial';
    ctx.textBaseline = 'top';

    detections.forEach(({ x_min, y_min, x_max, y_max, class_name }) => {
      const tagPosition = { x: x_min, y: y_max, z: 0 };
      position.push(tagPosition);

      const boxWidth = x_max - x_min;
      const boxHeight = y_max - y_min;

      // Draw bounding box
      ctx.strokeRect(x_min, y_min, boxWidth, boxHeight);
    });
    return position;
  }

  updateFilePreview(fileKey, newPreviewUrl) {
    const container = document.querySelector(`[data-file-key="${fileKey}"]`);
    if (!container) return;

    const imgElement = container.querySelector('img');
    if (imgElement) {
      imgElement.src = newPreviewUrl;
    }
  }

  removeFilePreviewElement(fileKey) {
    const previewElement = this.previewElementsMap.get(fileKey);
    if (previewElement) {
      previewElement.addClass('opacity-0 scale-95');

      setTimeout(() => {
        this.selectedFilesContainer.remove(previewElement);
        this.previewElementsMap.delete(fileKey);

        if (this.selectedFiles.size === 0) {
          this.uploadInstructionsContainer.setClass(
            'flex flex-col items-center justify-center py-4',
          );
        }

        this.updateModalWidth();
      }, 300);
    }
  }

  markFileUploading(fileKey) {
    if (this.selectedFiles.has(fileKey)) {
      const fileData = this.selectedFiles.get(fileKey);
      fileData.isUploading = true;
      this.selectedFiles.set(fileKey, fileData);
      const previewElement = this.previewElementsMap.get(fileKey);
      if (previewElement) {
        previewElement.addClass('bg-blue-50 loading-border');
      }
    }
  }

  markFileUploaded(fileKey) {
    if (this.selectedFiles.has(fileKey)) {
      const fileData = this.selectedFiles.get(fileKey);
      fileData.isUploaded = true;
      fileData.isUploading = false;
      this.selectedFiles.set(fileKey, fileData);
      const previewElement = this.previewElementsMap.get(fileKey);
      if (previewElement) {
        previewElement.removeClass('bg-blue-50 loading-border');
        previewElement.addClass('bg-green-50');
      }
    }
  }

  setupHandlers() {
    // --- Event handlers for file input ---
    this.onClick(event => {
      event.stopPropagation();
      this.fileInput.dom.click();
    });
    this.fileInput.onChange(event => {
      event.stopPropagation();
      this.handleFileSelection(event.target.files);
    });
    this.onDrop(event => {
      event.preventDefault();
      event.stopPropagation();
      this.handleFileSelection(event.dataTransfer.files);
      this.removeClass('bg-gray-100');
    });
    this.onDragOver(event => {
      event.preventDefault();
      event.stopPropagation();
      this.addClass('bg-gray-100');
    });
    this.onDragLeave(event => {
      event.stopPropagation();
      this.removeClass('bg-gray-100');
    });
  }
}
